export interface TemplateValidationResult {
  isValid: boolean;
  error?: string;
  usedFields: string[];
}

/**
 * Validates a template string for safety and correctness
 * @param template The template string to validate
 * @param availableFields Array of field names that are available for use
 * @returns Validation result with error message if invalid
 */
export function validateTemplate(
  template: string,
  availableFields: string[]
): TemplateValidationResult {
  const usedFields: string[] = [];
  
  // Check for empty template
  if (!template || template.trim().length === 0) {
    return {
      isValid: false,
      error: 'Template cannot be empty',
      usedFields: []
    };
  }

  // Check maximum length
  if (template.length > 500) {
    return {
      isValid: false,
      error: 'Template is too long (maximum 500 characters)',
      usedFields: []
    };
  }

  // Check for potentially harmful content
  const dangerousPatterns = [
    /<script/i,
    /<\/script>/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /javascript:/i,
    /on\w+\s*=/i, // onclick=, onload=, etc.
    /<img/i,
    /eval\(/i,
    /Function\(/i
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(template)) {
      return {
        isValid: false,
        error: 'Template contains potentially unsafe content',
        usedFields: []
      };
    }
  }

  // Check for balanced braces
  let braceCount = 0;
  let inField = false;
  let currentField = '';
  
  for (let i = 0; i < template.length; i++) {
    const char = template[i];
    
    if (char === '{') {
      if (inField) {
        return {
          isValid: false,
          error: 'Nested braces are not allowed',
          usedFields: []
        };
      }
      braceCount++;
      inField = true;
      currentField = '';
    } else if (char === '}') {
      braceCount--;
      if (braceCount < 0) {
        return {
          isValid: false,
          error: 'Unmatched closing brace',
          usedFields: []
        };
      }
      if (inField) {
        // Validate field name
        if (currentField.length === 0) {
          return {
            isValid: false,
            error: 'Empty field name in braces',
            usedFields: []
          };
        }
        
        // Check if field name contains only valid characters (now supporting table.field format)
        if (!/^[a-zA-Z0-9_.]+$/.test(currentField)) {
          return {
            isValid: false,
            error: `Invalid field name: {${currentField}}. Field names must be in table.field format with letters, numbers, dots, and underscores`,
            usedFields: []
          };
        }
        
        // Check if field exists in available fields
        if (!availableFields.includes(currentField)) {
          return {
            isValid: false,
            error: `Unknown field: {${currentField}}`,
            usedFields: []
          };
        }
        
        if (!usedFields.includes(currentField)) {
          usedFields.push(currentField);
        }
        
        inField = false;
        currentField = '';
      }
    } else if (inField) {
      currentField += char;
    }
  }
  
  if (braceCount !== 0) {
    return {
      isValid: false,
      error: 'Unmatched opening brace',
      usedFields: []
    };
  }

  // Check for allowed characters outside of field placeholders
  const templateWithoutFields = template.replace(/\{[^}]+\}/g, '');
  const allowedCharsPattern = /^[a-zA-Z0-9\s\-.,!?:;'"()/@#$%&*+=_\n\r]*$/;
  
  if (!allowedCharsPattern.test(templateWithoutFields)) {
    return {
      isValid: false,
      error: 'Template contains invalid characters',
      usedFields: []
    };
  }

  return {
    isValid: true,
    usedFields
  };
}

/**
 * Extracts field names from a template string
 * @param template The template string
 * @returns Array of field names found in the template (in table.field format)
 */
export function extractTemplateFields(template: string): string[] {
  const fields: string[] = [];
  const regex = /\{([\w.]+)\}/g;
  let match;
  
  while ((match = regex.exec(template)) !== null) {
    const fieldName = match[1];
    if (fieldName && !fields.includes(fieldName)) {
      fields.push(fieldName);
    }
  }
  
  return fields;
}